import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { useSelector } from 'react-redux'
import { getSelectedCustomerId } from '../selectors/customers'
import prepareHeaders from '../utils/prepareHeaders'

// Module scope so a missing/loading list keeps one stable reference for downstream useMemo deps.
const EMPTY_AUTHENTICATION_CONTEXTS = []

export const policyTemplateAuthenticationContextRequirementsApi = createApi({
  reducerPath: 'policyTemplateAuthenticationContextRequirements',
  baseQuery: fetchBaseQuery({
    prepareHeaders,
    baseUrl: process.env.REACT_APP_AUGMENTT_API_URL,
  }),
  endpoints: (builder) => ({
    getAuthenticationContextRequirementsPreview: builder.query({
      query: ({ templateId, customerId }) => ({
        url: `/secure/policy/template/${templateId}/authentication-context-requirements/preview`,
        params: { customerId },
      }),
    }),
    getTenantAuthenticationContexts: builder.query({
      query: ({ customerId }) => ({
        url: '/secure/policy/template/authentication-contexts',
        params: { customerId },
      }),
    }),
  }),
})

export const {
  useGetAuthenticationContextRequirementsPreviewQuery,
  useGetTenantAuthenticationContextsQuery,
  useLazyGetTenantAuthenticationContextsQuery,
} = policyTemplateAuthenticationContextRequirementsApi

export const useAuthenticationContextRequirementsPreview = (
  { templateId, customerId: customerIdOverride },
  options = {},
) => {
  const selectedCustomerId = useSelector(getSelectedCustomerId)
  // Deploy from a check can target a different tenant than the globally selected customer.
  // Callers pass that tenant; otherwise fall back to the global selection.
  const customerId = customerIdOverride ?? selectedCustomerId

  // Deploy resolves against the tenant's live contexts, so every review must too — a cached
  // result could show "Will be adopted" after the template or the tenant's contexts changed.
  return useGetAuthenticationContextRequirementsPreviewQuery(
    { templateId, customerId },
    { refetchOnMountOrArgChange: true, ...options, skip: options.skip || !templateId || !customerId },
  )
}

// A tenant's authentication contexts ({ id, displayName }) for the policy editor's options, so a
// context shows by name instead of its opaque c1–c99 id. currentData, not data, so switching
// tenants never shows the previous tenant's contexts.
export const useTenantAuthenticationContexts = (customerId) => {
  const { currentData } = useGetTenantAuthenticationContextsQuery({ customerId }, { skip: !customerId })
  return currentData?.authenticationContexts ?? EMPTY_AUTHENTICATION_CONTEXTS
}
